import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { Topic, TopicSort } from '@mmf/contracts';
import { fetchTopics } from '../api/client';
import { moderationBadgeShortText } from '../forum/moderationBadge';

const PREVIEW_COUNT = 5;

// A Record keyed by the (exhaustive, literal-union) TopicSort type, rather
// than an array searched with .find() — indexing this by activeFeed is
// always defined, no fallback-for-a-lookup-that-can't-actually-fail needed.
const FEED_META: Record<TopicSort, { label: string; testId: string }> = {
  recent: { label: 'Latest', testId: 'latest' },
  top: { label: 'Top', testId: 'top' },
  trending: { label: 'Trending', testId: 'trending' },
  recentlyUpdated: { label: 'Recently Updated', testId: 'recently-updated' },
};
const FEED_ORDER: TopicSort[] = ['recent', 'top', 'trending', 'recentlyUpdated'];

/**
 * One focused list at a time, switched by tab, rather than four parallel
 * columns of the same shape side by side. Four simultaneous lists forced
 * the eye to scan across the whole width of the page just to read one —
 * and with a small forum, the same handful of topics tend to show up in
 * more than one feed anyway, which reads as duplication rather than four
 * genuinely different views. A tab keeps all four feeds one click away
 * without asking for four times the vertical/horizontal space up front.
 *
 * Same viewer-scoped visibility as everywhere else that calls fetchTopics:
 * the API resolves the signed-in principal server-side and only ever returns
 * Published topics plus the viewer's own held/rejected ones — nobody else's
 * hidden content can appear here. But *your own* held/rejected topics can
 * legitimately show up in "Latest"/"Top" alongside everyone else's public
 * ones, so each row needs the same badge TopicList/TopicDetail use — without
 * it, a rejected post looks exactly like a real, publicly-trending one.
 */
export function FeaturedTopics(): React.JSX.Element {
  const [activeFeed, setActiveFeed] = useState<TopicSort>('recent');

  // All four are fetched up front (plain, explicit hook calls — not looped
  // over FEEDS, so there's no risk of an inconsistent hook count) so
  // switching tabs is instant after the first load, rather than re-fetching
  // on every click.
  const recent = useQuery({ queryKey: ['topics', 'all', 'recent', false], queryFn: () => fetchTopics({ sort: 'recent' }) });
  const top = useQuery({ queryKey: ['topics', 'all', 'top', false], queryFn: () => fetchTopics({ sort: 'top' }) });
  const trending = useQuery({
    queryKey: ['topics', 'all', 'trending', false],
    queryFn: () => fetchTopics({ sort: 'trending' }),
  });
  const recentlyUpdated = useQuery({
    queryKey: ['topics', 'all', 'recentlyUpdated', false],
    queryFn: () => fetchTopics({ sort: 'recentlyUpdated' }),
  });
  const queriesBySort: Record<TopicSort, ReturnType<typeof useQuery<{ items: Topic[] }>>> = {
    recent,
    top,
    trending,
    recentlyUpdated,
  };

  return (
    <section aria-labelledby="featured-topics-heading">
      <div className="topic-list-header">
        <h2 id="featured-topics-heading">Featured Topics</h2>
        <Link to="/discussions/all" className="back-link">
          View all discussions
        </Link>
      </div>

      <div className="featured-topics-tabs" role="group" aria-label="Choose a feed">
        {FEED_ORDER.map((sort) => (
          <button
            key={sort}
            type="button"
            className="featured-topics-tab"
            aria-pressed={sort === activeFeed}
            onClick={() => setActiveFeed(sort)}
            data-testid={`featured-tab-${FEED_META[sort].testId}`}
          >
            {FEED_META[sort].label}
          </button>
        ))}
      </div>

      <TopicPreviewList
        query={queriesBySort[activeFeed]}
        emptyLabel="No discussions yet."
        testIdPrefix={FEED_META[activeFeed].testId}
      />
    </section>
  );
}

function formatLastActivity(date: Date): string {
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function TopicPreviewList({
  query,
  emptyLabel,
  testIdPrefix,
}: {
  query: { isLoading: boolean; error: unknown; data?: { items: Topic[] } };
  emptyLabel: string;
  testIdPrefix: string;
}): React.JSX.Element {
  if (query.isLoading) {
    return <p role="status">Loading…</p>;
  }

  if (query.error) {
    return (
      <p role="alert" data-testid={`${testIdPrefix}-topics-error`}>
        Could not load discussions.
      </p>
    );
  }

  const items = query.data?.items.slice(0, PREVIEW_COUNT) ?? [];

  if (items.length === 0) {
    return <p className="hint">{emptyLabel}</p>;
  }

  return (
    <ul className="featured-topic-list">
      {items.map((topic) => (
        <li key={topic.id} className="featured-topic-row">
          <div className="featured-topic-title-row">
            <Link to={`/topics/${topic.id}`} data-testid={`${testIdPrefix}-topic-link-${topic.id}`}>
              {topic.title}
            </Link>
            {moderationBadgeShortText(topic.moderationState) && (
              <span className="badge" data-testid={`${testIdPrefix}-held-badge-${topic.id}`}>
                {moderationBadgeShortText(topic.moderationState)}
              </span>
            )}
          </div>
          <span className="hint">
            {topic.categoryName} · {topic.commentCount} comment
            {topic.commentCount === 1 ? '' : 's'} ·{' '}
            <span data-testid={`${testIdPrefix}-last-activity-${topic.id}`}>
              {formatLastActivity(topic.lastActivityAt)}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
